// Global Instructions Rule Applied!
//
// Agent Signals section — fetches a subject's signals (company or deal) and
// renders the pilot-format cards. S-refs are the per-subject chronological
// index over the FULL signal history (all statuses, fired_at ascending) so a
// ref stays stable as signals resolve. Used by the Dashboards (open only,
// maxVisible cap) and the Agent tab (full history).
//
// ONE SHAPE EVERYWHERE (owner, 2026-09-06). This used to have a second,
// chrome-less render for embedding inside a states strip's footer, which is
// how the company, LP, deal, execution and prospect Dashboards drew it — so
// the same signals appeared as a bare list on five surfaces and as a titled
// section on three others, and on the busiest of them they sat between the
// state cards and the drafts that answer them. Every caller now renders the
// section, with its own heading, open-count Tag and collapse chevron.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Input, Tag } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useSectionCollapse } from '../../../core/hooks/useSectionCollapse.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  createTaskFromSignal,
  fetchAgentSignals,
  setAgentSignalStatus
} from '../services/internalAgentsService.ts'
import { TaskForm, TaskFormRef } from '../../tasks/forms/TaskForm.tsx'
import { AgentSignal } from '../../../state_manager/data_models/internal_agents_model.ts'
import { AgentSignalCard, SEVERITY_STYLES } from './AgentSignalCard.tsx'
import { useOpenInteraction } from '../hooks/useOpenInteraction.ts'

interface AgentSignalsSectionProps {
  /** Subject the signals hang on: 'accounts' (default) or 'deals'. */
  contextTable?: string
  contextId: string
  // Dashboard strip: open signals only, capped; Agent tab: everything.
  openOnly?: boolean
  // Phase 3 (owner decision): once states ship, the Dashboard strip narrows
  // to crit severity — the states strip carries the routine picture.
  critOnly?: boolean
  maxVisible?: number
  maxEvidencePerCard?: number
  onOpenInteraction?: (interactionId: string) => void
  // Dashboard strip renders nothing at all when there are no open signals
  // (non-portfolio accounts and quiet companies keep today's layout).
  hideWhenEmpty?: boolean
  title?: string
  /** Hands `load` to the company form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Surface key for the collapsed/expanded preference (e.g.
   *  'lp_dashboard_signals'). Absent = always expanded, no chevron. */
  collapseKey?: string
  /** Starting position before the user has folded this surface. Signals
   *  default OPEN — they are the attention layer. */
  defaultCollapsed?: boolean
  /** Shown as a "View more" link when more open signals exist than are
   *  visible; the Dashboard points this at the Agent tab. */
  onViewMore?: () => void
  /** Pre-selects assignees in the create-task form (the deal form passes its
   *  deal lead). Empty leaves the picker blank — never auto-assigns. */
  defaultAssignees?: Array<{ label: string; value: string }>
  /** Fired after a task is created so the host can refresh its Tasks panel. */
  onTaskCreated?: () => void
  /** Restrict VISIBLE signals to these agent types (deal split: the Dashboard
   *  shows the origination follower's, the Execution tab the execution
   *  follower's). A DISPLAY filter only — the fetch stays full-history so the
   *  S-ref numbering matches the Agent tab. */
  agentTypeKeys?: string[]
}

export const AgentSignalsSection: React.FC<AgentSignalsSectionProps> = ({
  contextTable = 'accounts',
  contextId,
  openOnly = false,
  critOnly = false,
  maxVisible,
  maxEvidencePerCard = 1,
  onOpenInteraction,
  hideWhenEmpty = false,
  title = 'Agent Signals',
  onRefreshReady = null,
  collapseKey,
  defaultCollapsed = false,
  onViewMore,
  defaultAssignees = [],
  onTaskCreated,
  agentTypeKeys
}) => {
  const { collapsed, setCollapsed, chevron } = useSectionCollapse(collapseKey, defaultCollapsed)
  // Cited interactions open on this context's timeline unless the host
  // supplies its own handler. Undefined (no timeline: funds, fund groups,
  // meeting series) makes the cards render citations as plain text.
  const defaultOpenInteraction = useOpenInteraction(contextTable, contextId)
  const openInteraction = onOpenInteraction ?? defaultOpenInteraction
  const [signals, setSignals] = useState<AgentSignal[]>([])
  const [loading, setLoading] = useState(true)
  const [dismissing, setDismissing] = useState<AgentSignal | null>(null)
  const [dismissReason, setDismissReason] = useState('')
  // Task-from-signal: always a person's decision (the agent never creates
  // tasks), so the signal only pre-fills a form the user reviews.
  const [taskFor, setTaskFor] = useState<AgentSignal | null>(null)
  const [creatingTask, setCreatingTask] = useState(false)
  const taskFormRef = useRef<TaskFormRef>(null)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Always fetch full history: the S-ref numbering needs it.
      setSignals(
        await fetchAgentSignals(
          { context_table: contextTable, context_id: contextId, limit: 500 },
          token()
        )
      )
    } catch (error) {
      console.error('[internal-agents] signals load failed:', error)
      setSignals([])
    } finally {
      setLoading(false)
    }
  }, [contextTable, contextId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // company form so its Refresh button reloads this panel too (same contract as
  // DocumentsTab / FileUploadManager).
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  // fired_at ASC index over the full history = stable S-numbers.
  const sequenceById = useMemo(() => {
    const ordered = [...signals].sort((a, b) =>
      a.agent_signals__fired_at.localeCompare(b.agent_signals__fired_at)
    )
    const map = new Map<string, number>()
    ordered.forEach((signal, index) => map.set(signal.agent_signals__id, index + 1))
    return map
  }, [signals])

  const visible = useMemo(() => {
    let pool = openOnly ? signals.filter((s) => s.agent_signals__status === 'open') : signals
    if (critOnly) pool = pool.filter((s) => s.agent_signals__severity === 'crit')
    if (agentTypeKeys?.length) {
      pool = pool.filter(
        (s) => s.agent_type_key != null && agentTypeKeys.includes(s.agent_type_key)
      )
    }
    const ordered = [...pool].sort((a, b) =>
      b.agent_signals__fired_at.localeCompare(a.agent_signals__fired_at)
    )
    return maxVisible ? ordered.slice(0, maxVisible) : ordered
  }, [signals, openOnly, critOnly, maxVisible, agentTypeKeys])

  const openCount = useMemo(
    () =>
      signals.filter(
        (s) =>
          s.agent_signals__status === 'open' &&
          (!critOnly || s.agent_signals__severity === 'crit') &&
          (!agentTypeKeys?.length ||
            (s.agent_type_key != null && agentTypeKeys.includes(s.agent_type_key)))
      ).length,
    [signals, critOnly, agentTypeKeys]
  )

  // Optimistic: flip the local row first so the card reacts instantly, then
  // confirm against the server (reload restores truth on failure).
  const _applyLocal = (signalId: string, status: string) => {
    setSignals((prev) =>
      prev.map((s) =>
        s.agent_signals__id === signalId ? { ...s, agent_signals__status: status } : s
      )
    )
  }

  const handleAcknowledge = async (signalId: string) => {
    _applyLocal(signalId, 'acknowledged')
    try {
      await setAgentSignalStatus(signalId, 'acknowledged', null, token())
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update signal')
      await load()
    }
  }

  const handleDismissConfirm = async () => {
    if (!dismissing) return
    const signalId = dismissing.agent_signals__id
    setDismissing(null)
    _applyLocal(signalId, 'dismissed')
    try {
      await setAgentSignalStatus(signalId, 'dismissed', dismissReason.trim() || null, token())
      setDismissReason('')
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to dismiss signal')
      await load()
    }
  }

  const handleCreateTaskConfirm = async () => {
    if (!taskFor || !taskFormRef.current) return
    let taskRecord: unknown
    try {
      // TaskForm owns validation and mints the task id, exactly as the
      // Dashboard's add-task flow does.
      taskRecord = await taskFormRef.current.validateFields()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Please check the task details')
      return
    }
    setCreatingTask(true)
    try {
      await createTaskFromSignal(taskFor.agent_signals__id, taskRecord, token())
      setTaskFor(null)
      toast.success('Task created')
      await load()
      // The deal/company Tasks section is a sibling panel with its own fetch.
      onTaskCreated?.()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to create task')
    } finally {
      setCreatingTask(false)
    }
  }

  // Pre-fill from the signal: its title is already a one-line statement of
  // what happened, and the summary is the context whoever picks the task up
  // will want. The user edits both before saving.
  const taskInitialValues = useMemo(
    () =>
      taskFor
        ? {
            subject: (taskFor.agent_signals__title || '').slice(0, 80),
            notes: taskFor.agent_signals__summary || '',
            assignedTo: defaultAssignees,
            dueDate: null,
            completed: false
          }
        : {},
    [taskFor, defaultAssignees]
  )

  const taskModal = (
    <Modal
      title='Create task from signal'
      open={taskFor != null}
      onOk={handleCreateTaskConfirm}
      okText='Create task'
      confirmLoading={creatingTask}
      onCancel={() => setTaskFor(null)}
      destroyOnClose
      width={560}
    >
      <p className='text-gray-600'>
        Pre-filled from the signal — edit anything before saving. The task is linked to this{' '}
        {contextTable === 'deals' ? 'deal' : 'company'} and appears in its Tasks list; the signal is
        marked acknowledged.
      </p>
      {/* TaskForm is built for the full-height detail panel: its root is
          `h-full flex flex-col` with the notes editor as flex-1, so left
          unbounded it grows past 2000px. Bounding the wrapper lets the
          editor size itself to what is left — compact, same controls. */}
      <div style={{ height: 430 }}>
        <TaskForm ref={taskFormRef} initialValues={taskInitialValues} />
      </div>
    </Modal>
  )

  if (hideWhenEmpty && !loading && (openOnly ? openCount === 0 : signals.length === 0)) {
    return null
  }

  const dismissModal = (
    <Modal
      title='Dismiss signal'
      open={dismissing != null}
      onOk={handleDismissConfirm}
      okText='Dismiss'
      okButtonProps={{ danger: true }}
      onCancel={() => setDismissing(null)}
      destroyOnClose
    >
      <p className='text-gray-600'>
        Dismissals mark the signal wrong or noisy and feed threshold tuning — nothing is assigned
        to anyone. Reason optional.
      </p>
      <Input.TextArea
        rows={2}
        value={dismissReason}
        onChange={(e) => setDismissReason(e.target.value)}
        placeholder='Why is this wrong/noisy? (optional)'
      />
    </Modal>
  )

  // More open signals than the maxVisible cap is showing — the Dashboard turns
  // this into a "View more" link through to the Agent tab's full history.
  const hiddenCount = openCount - visible.length
  const viewMore =
    onViewMore && hiddenCount > 0 ? (
      <div className='flex justify-end pt-1'>
        <Button type='link' size='small' className='px-0' onClick={onViewMore}>
          View more
        </Button>
      </div>
    ) : null

  // Collapsed: one line per signal — severity, kind and title — rather than an
  // empty section (owner, 2026-09-06). Folding is for reclaiming height, not
  // for hiding what is open, and the count Tag alone does not say WHICH
  // signals are waiting. The states strip's pill row is the precedent, down to
  // clicking a row to unfold.
  const summaryRows = (
    <div className='flex flex-col'>
      {visible.map((signal) => {
        const sev = SEVERITY_STYLES[signal.agent_signals__severity] ?? SEVERITY_STYLES.info
        return (
          <div
            key={signal.agent_signals__id}
            role='button'
            tabIndex={0}
            title={signal.agent_signals__summary ?? signal.agent_signals__title ?? ''}
            className='cursor-pointer flex items-baseline gap-2 py-1 text-[12px] min-w-0 hover:bg-gray-50 rounded'
            onClick={() => setCollapsed(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setCollapsed(false)
            }}
          >
            <span
              className='flex-none rounded-full text-[9.5px] font-bold uppercase tracking-wider px-1.5'
              style={{ color: sev.pillColor, background: sev.pillBg }}
            >
              {signal.agent_signals__severity}
            </span>
            <span className='flex-none text-gray-500'>{signal.kind_label}</span>
            <span className='truncate text-gray-700'>{signal.agent_signals__title}</span>
            <span className='ml-auto flex-none text-gray-400 text-[11px] tabular-nums'>
              {signal.agent_signals__fired_at ? dayjs(signal.agent_signals__fired_at).format('MM/DD/YY') : null}
            </span>
          </div>
        )
      })}
      {viewMore}
    </div>
  )

  return (
    <FormSection
      data-component='AgentSignalsSection'
      title={title}
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerLeading={chevron}
      headerActions={
        openCount > 0 ? <Tag color='red'>{openCount} open</Tag> : <Tag>none open</Tag>
      }
    >
      {visible.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-1'>No signals.</div>
      ) : collapsed ? (
        summaryRows
      ) : (
        <div className='flex flex-col gap-2.5'>
          {visible.map((signal) => (
            <AgentSignalCard
              key={signal.agent_signals__id}
              signal={signal}
              sequenceNumber={sequenceById.get(signal.agent_signals__id) ?? 0}
              maxEvidence={maxEvidencePerCard}
              onAcknowledge={handleAcknowledge}
              onDismiss={() => {
                setDismissing(signal)
                setDismissReason('')
              }}
              onCreateTask={(target) => setTaskFor(target)}
              onOpenInteraction={openInteraction}
            />
          ))}
          {viewMore}
        </div>
      )}

      {dismissModal}
      {taskModal}
    </FormSection>
  )
}
